import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Customer, CustomerCreate, Vendor, VendorCreate,
  getCustomers, createCustomer, updateCustomer, deleteCustomer,
  getVendors, createVendor, updateVendor, deleteVendor,
} from '../services/clearanceService';
import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';
import { useFunctionName } from '../hooks/useFunctionName';
import { logView, logCreate, logUpdate, logDelete } from '../utils/userLogHelper';
import { validateSession } from '../utils/sessionValidator';
import FunctionPageHeader from '../components/FunctionPageHeader';
import '../styles/DataTable.css';

const EMPTY_FORM: CustomerCreate = {
  tax_id: '', name_zh: '', name_en: '',
  address_zh: '', address_en: '',
  customs_monitor_code: '', aeo_code: '', bonded_factory_code: '',
  default_declaration_type: 'B6', default_storage_code: '', default_trade_condition: '',
  contact_name: '', contact_phone: '', contact_email: '',
};

const EMPTY_VENDOR: VendorCreate = {
  name_en: '', name_zh: '', country_code: '', address_en: '',
  contact_name: '', contact_email: '', notes: '',
};

// ─── Vendor Sub-panel ─────────────────────────────────────────────────────────

const VendorPanel: React.FC<{ customer: Customer; canUpdate: boolean }> = ({ customer, canUpdate }) => {
  const { t } = useTranslation();
  const { showApiError, showSuccess } = useMessage();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [form, setForm] = useState<VendorCreate>(EMPTY_VENDOR);

  const load = async () => {
    setLoading(true);
    try { setVendors(await getVendors(customer.id)); }
    catch (err: any) { showApiError(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [customer.id]);

  const openForm = (v?: Vendor) => {
    if (v) {
      setEditingVendor(v);
      setForm({ name_en: v.name_en, name_zh: v.name_zh || '', country_code: v.country_code || '', address_en: v.address_en || '', contact_name: v.contact_name || '', contact_email: v.contact_email || '', notes: v.notes || '' });
    } else {
      setEditingVendor(null); setForm(EMPTY_VENDOR);
    }
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name_en.trim()) return;
    try {
      if (editingVendor) {
        await updateVendor(customer.id, editingVendor.id, form);
        showSuccess('SYS020002', { name: t('clearance.vendor.title', '賣家') });
      } else {
        await createVendor(customer.id, form);
        showSuccess('SYS020001', { name: t('clearance.vendor.title', '賣家') });
      }
      setShowForm(false); load();
    } catch (err: any) { showApiError(err); }
  };

  const handleDelete = async (v: Vendor) => {
    if (!window.confirm(`確定停用賣家 ${v.name_en}？`)) return;
    try {
      await deleteVendor(customer.id, v.id);
      showSuccess('SYS020003', { name: t('clearance.vendor.title', '賣家') });
      load();
    } catch (err: any) { showApiError(err); }
  };

  const set = (k: keyof VendorCreate, v: string) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ padding: '12px 16px', background: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: '#495057' }}>
          🏭 外國賣家清單（{vendors.length} 家）
        </span>
        {canUpdate && !showForm && (
          <button className="btn-primary" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => openForm()}>
            + 新增賣家
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 6, padding: 14, marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <VField label="英文名稱 *" value={form.name_en} onChange={v => set('name_en', v)} />
            <VField label="中文別名" value={form.name_zh || ''} onChange={v => set('name_zh', v)} />
            <VField label="國家代碼 (ISO-2)" value={form.country_code || ''} onChange={v => set('country_code', v)} placeholder="US / CN / JP..." />
            <VField label="英文地址" value={form.address_en || ''} onChange={v => set('address_en', v)} />
            <VField label="聯絡人" value={form.contact_name || ''} onChange={v => set('contact_name', v)} />
            <VField label="Email" value={form.contact_email || ''} onChange={v => set('contact_email', v)} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn-secondary" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => setShowForm(false)}>取消</button>
            <button type="submit" className="btn-primary" style={{ padding: '3px 10px', fontSize: 12 }}>儲存</button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={{ fontSize: 13, color: '#6c757d', padding: '8px 0' }}>載入中...</div>
      ) : vendors.length === 0 ? (
        <div style={{ fontSize: 13, color: '#6c757d', padding: '8px 0' }}>尚無賣家資料。上傳報關文件後，系統可自動建立。</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#e9ecef' }}>
              <th style={thS}>英文名稱</th>
              <th style={thS}>中文別名</th>
              <th style={thS}>國家</th>
              <th style={thS}>聯絡人</th>
              <th style={thS}>狀態</th>
              {canUpdate && <th style={thS}>操作</th>}
            </tr>
          </thead>
          <tbody>
            {vendors.map(v => (
              <tr key={v.id} style={{ borderBottom: '1px solid #dee2e6', opacity: v.is_active ? 1 : 0.5 }}>
                <td style={tdS}><strong>{v.name_en}</strong></td>
                <td style={tdS}>{v.name_zh || '—'}</td>
                <td style={tdS}>{v.country_code || '—'}</td>
                <td style={tdS}>{v.contact_name || '—'}</td>
                <td style={tdS}>
                  <span style={{ color: v.is_active ? '#28a745' : '#6c757d', fontWeight: 600 }}>
                    {v.is_active ? '啟用' : '停用'}
                  </span>
                </td>
                {canUpdate && (
                  <td style={tdS}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn-secondary" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => openForm(v)}>編輯</button>
                      <button className="btn-danger" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => handleDelete(v)}>停用</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const VField: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder?: string }> =
  ({ label, value, onChange, placeholder }) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, fontWeight: 500 }}>
      {label}
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ padding: '5px 7px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 12 }} />
    </label>
  );

const thS: React.CSSProperties = { padding: '6px 10px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' };
const tdS: React.CSSProperties = { padding: '6px 10px', verticalAlign: 'middle' };

// ─── Main Page ─────────────────────────────────────────────────────────────────

const ClearanceCustomersPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showError, showApiError } = useMessage();
  const pageTitle = useFunctionName('ee_clearance_customers');
  const hasInit = useRef(false);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filtered, setFiltered] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedVendor, setExpandedVendor] = useState<number | null>(null);
  const perPage = 15;

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [isView, setIsView] = useState(false);
  const [form, setForm] = useState<CustomerCreate>(EMPTY_FORM);

  useEffect(() => {
    if (!permLoading && !hasInit.current) {
      hasInit.current = true;
      const init = async () => {
        const valid = await validateSession('ClearanceCustomersPage');
        if (!valid) return;
        await load();
        await logView('ee_clearance_customers', undefined, undefined);
      };
      init();
    }
  }, [permLoading]);

  useEffect(() => {
    let result = customers;
    if (!showInactive) result = result.filter(c => c.is_active);
    if (keyword.trim()) {
      const kw = keyword.toLowerCase();
      result = result.filter(c =>
        c.tax_id.includes(kw) || c.name_zh.toLowerCase().includes(kw) ||
        c.name_en.toLowerCase().includes(kw) || (c.short_code || '').toLowerCase().includes(kw)
      );
    }
    setFiltered(result);
    setCurrentPage(1);
  }, [customers, keyword, showInactive]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (err: any) { showApiError(err); }
    finally { setLoading(false); }
  };

  const openModal = (c?: Customer, view = false) => {
    if (c) {
      setEditing(c); setIsView(view);
      setForm({
        tax_id: c.tax_id, name_zh: c.name_zh, name_en: c.name_en,
        address_zh: c.address_zh || '', address_en: c.address_en || '',
        customs_monitor_code: c.customs_monitor_code || '',
        aeo_code: c.aeo_code || '', bonded_factory_code: c.bonded_factory_code || '',
        default_declaration_type: c.default_declaration_type,
        default_storage_code: c.default_storage_code || '',
        default_trade_condition: c.default_trade_condition || '',
        contact_name: c.contact_name || '',
        contact_phone: c.contact_phone || '',
        contact_email: c.contact_email || '',
      });
    } else {
      setEditing(null); setIsView(false); setForm(EMPTY_FORM);
    }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.tax_id || !form.name_zh || !form.name_en) {
      showError('ERR020006', { field: t('clearance.customer.requiredFields', '統一編號、中英文名稱') });
      return;
    }
    try {
      if (editing) {
        const updated = await updateCustomer(editing.id, form);
        await logUpdate('ee_clearance_customers', editing, updated);
        showSuccess('SYS020002', { name: pageTitle });
      } else {
        const created = await createCustomer(form);
        await logCreate('ee_clearance_customers', created);
        showSuccess('SYS020001', { name: pageTitle });
      }
      closeModal(); load();
    } catch (err: any) { showApiError(err); }
  };

  const handleToggleActive = async (c: Customer) => {
    if (!hasPermission('ee_clearance_customers', 'update')) return;
    try {
      await updateCustomer(c.id, { is_active: !c.is_active });
      load();
    } catch (err: any) { showApiError(err); }
  };

  const handleDelete = async (c: Customer) => {
    if (!hasPermission('ee_clearance_customers', 'delete')) return;
    if (!window.confirm(t('clearance.customer.confirmDelete', `確定要刪除客戶 ${c.name_zh} 嗎？`))) return;
    try {
      await deleteCustomer(c.id);
      await logDelete('ee_clearance_customers', c);
      showSuccess('SYS020003', { name: pageTitle });
      load();
    } catch (err: any) { showApiError(err); }
  };

  const set = (k: keyof CustomerCreate, v: string) => setForm(f => ({ ...f, [k]: v }));

  if (permLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('ee_clearance_customers', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;

  const canCreate = hasPermission('ee_clearance_customers', 'create');
  const canUpdate = hasPermission('ee_clearance_customers', 'update');
  const canDelete = hasPermission('ee_clearance_customers', 'delete');

  const totalPages = Math.ceil(filtered.length / perPage);
  const pageData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="ee_clearance_customers" />
        {canCreate && <button className="btn-primary" onClick={() => openModal()}>{t('common.create')}</button>}
      </div>

      {/* 搜尋 */}
      <div className="search-bar" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={keyword} onChange={e => setKeyword(e.target.value)}
          placeholder={t('clearance.customer.searchPlaceholder', '搜尋統編、名稱、代號…')}
          style={{ flex: 1, minWidth: 200, padding: '7px 12px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          {t('clearance.customer.showInactive', '顯示停用')}
        </label>
        <button className="btn-secondary" onClick={() => { setKeyword(''); setShowInactive(false); }}>
          {t('common.reset')}
        </button>
      </div>

      {loading ? <div className="loading">{t('common.loading')}</div> : (
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('clearance.customer.taxId', '統一編號')}</th>
              <th>{t('clearance.customer.shortCode', '代號')}</th>
              <th>{t('clearance.customer.nameZh', '中文名稱')}</th>
              <th>{t('clearance.customer.nameEn', '英文名稱')}</th>
              <th>{t('clearance.customer.declType', '申報類別')}</th>
              <th>推論模式</th>
              <th>{t('clearance.customer.contact', '聯絡人')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: '#6c757d' }}>{t('common.noData')}</td></tr>
            ) : pageData.map(c => (
              <React.Fragment key={c.id}>
                <tr style={{ opacity: c.is_active ? 1 : 0.5 }}>
                  <td><strong>{c.tax_id}</strong></td>
                  <td>{c.short_code}</td>
                  <td>{c.name_zh}</td>
                  <td style={{ fontSize: 12, color: '#555' }}>{c.name_en}</td>
                  <td><span style={badgeStyle('#0d6efd')}>{c.default_declaration_type}</span></td>
                  <td>
                    <InferenceModeBadge mode={c.inference_mode} accuracy={c.local_model_accuracy} />
                  </td>
                  <td>{c.contact_name}{c.contact_phone ? ` ${c.contact_phone}` : ''}</td>
                  <td>
                    <span style={badgeStyle(c.is_active ? '#28a745' : '#6c757d')}>
                      {c.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="btn-secondary" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => openModal(c, true)}>{t('common.view')}</button>
                      {canUpdate && <button className="btn-secondary" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => openModal(c)}>{t('common.edit')}</button>}
                      <button
                        className="btn-secondary"
                        style={{ padding: '3px 10px', fontSize: 12, background: expandedVendor === c.id ? '#e8f4fd' : undefined }}
                        onClick={() => setExpandedVendor(expandedVendor === c.id ? null : c.id)}
                      >
                        🏭 賣家
                      </button>
                      {canUpdate && <button className="btn-secondary" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => handleToggleActive(c)}>{c.is_active ? t('common.inactive') : t('common.enable')}</button>}
                      {canDelete && <button className="btn-danger" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => handleDelete(c)}>{t('common.delete')}</button>}
                    </div>
                  </td>
                </tr>
                {expandedVendor === c.id && (
                  <tr>
                    <td colSpan={9} style={{ padding: 0 }}>
                      <VendorPanel customer={c} canUpdate={canUpdate} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {/* 分頁 */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 16 }}>
          {Array.from({ length: totalPages }, (_, i) => (
            <button key={i} onClick={() => setCurrentPage(i + 1)}
              style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #dee2e6', background: currentPage === i + 1 ? '#0d6efd' : '#fff', color: currentPage === i + 1 ? '#fff' : '#212529', cursor: 'pointer' }}>
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>
              {isView ? t('common.view') : editing ? t('common.edit') : t('common.create')} — {pageTitle}
            </h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <ModalField label={`${t('clearance.customer.taxId', '統一編號')} *`} value={form.tax_id} disabled={!!editing || isView} onChange={v => set('tax_id', v)} />
                <ModalField label={t('clearance.customer.shortCode', '內部代號')} value={form.short_code || ''} disabled={isView} onChange={v => set('short_code', v)} />
                <ModalField label={`${t('clearance.customer.nameZh', '中文名稱')} *`} value={form.name_zh} disabled={isView} onChange={v => set('name_zh', v)} />
                <ModalField label={`${t('clearance.customer.nameEn', '英文名稱')} *`} value={form.name_en} disabled={isView} onChange={v => set('name_en', v)} />
                <ModalField label={t('clearance.customer.addressZh', '中文地址')} value={form.address_zh || ''} disabled={isView} onChange={v => set('address_zh', v)} />
                <ModalField label={t('clearance.customer.addressEn', '英文地址')} value={form.address_en || ''} disabled={isView} onChange={v => set('address_en', v)} />
                <ModalField label={t('clearance.customer.customsMonitorCode', '海關監管編號')} value={form.customs_monitor_code || ''} disabled={isView} onChange={v => set('customs_monitor_code', v)} />
                <ModalField label={t('clearance.customer.aeoCode', 'AEO 優質企業')} value={form.aeo_code || ''} disabled={isView} onChange={v => set('aeo_code', v)} />
                <ModalField label={t('clearance.customer.bondedFactoryCode', '保稅工廠代碼')} value={form.bonded_factory_code || ''} disabled={isView} onChange={v => set('bonded_factory_code', v)} />
                <ModalField label={t('clearance.customer.declType', '預設申報類別')} value={form.default_declaration_type || ''} disabled={isView} onChange={v => set('default_declaration_type', v)} />
                <ModalField label={t('clearance.customer.tradeCondition', '預設貿易條件')} value={form.default_trade_condition || ''} disabled={isView} onChange={v => set('default_trade_condition', v)} placeholder="CIF/FOB/CFR" />
                <ModalField label={t('clearance.customer.contactName', '聯絡人')} value={form.contact_name || ''} disabled={isView} onChange={v => set('contact_name', v)} />
                <ModalField label={t('clearance.customer.contactPhone', '聯絡電話')} value={form.contact_phone || ''} disabled={isView} onChange={v => set('contact_phone', v)} />
                <ModalField label={t('clearance.customer.contactEmail', '聯絡信箱')} value={form.contact_email || ''} disabled={isView} onChange={v => set('contact_email', v)} />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" className="btn-secondary" onClick={closeModal}>{t('common.cancel')}</button>
                {!isView && <button type="submit" className="btn-primary">{t('common.save')}</button>}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Inference Mode Badge ──────────────────────────────────────────────────────

const InferenceModeBadge: React.FC<{ mode: string; accuracy?: number | null }> = ({ mode, accuracy }) => {
  const colors: Record<string, string> = { claude: '#6f42c1', local: '#28a745', hybrid: '#fd7e14' };
  const labels: Record<string, string> = { claude: 'Claude', local: '本地', hybrid: '混合' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
      <span style={badgeStyle(colors[mode] || '#6c757d')}>{labels[mode] || mode}</span>
      {accuracy != null && (
        <span style={{ fontSize: 10, color: accuracy >= 85 ? '#28a745' : '#fd7e14', fontWeight: 600 }}>
          {accuracy.toFixed(1)}%
        </span>
      )}
    </div>
  );
};

const ModalField: React.FC<{ label: string; value: string; disabled: boolean; onChange: (v: string) => void; placeholder?: string }> =
  ({ label, value, disabled, onChange, placeholder }) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13, fontWeight: 500 }}>
      {label}
      <input value={value} onChange={e => onChange(e.target.value)} disabled={disabled} placeholder={placeholder}
        style={{ padding: '6px 8px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 13, background: disabled ? '#f8f9fa' : '#fff' }} />
    </label>
  );

const badgeStyle = (bg: string): React.CSSProperties => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 10,
  background: bg, color: '#fff', fontSize: 11, fontWeight: 600,
});
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 8, padding: '24px 28px',
  width: '90%', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};

export default ClearanceCustomersPage;
